import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { WorkerAndToolService } from 'src/app/shared/services/worker-and-tool.service';
import { RestApiService } from 'src/app/shared/services/rest-api.service';
import { ToolbarService } from 'src/app/shared/services/toolbar.service';
import { Registration } from 'src/app/shared/models/registration';
import { DxDataGridComponent } from 'devextreme-angular';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { Material } from '../../shared/models/material';
import { HoursOption } from 'src/app/shared/models/hours-option';
import { Worker } from 'src/app/shared/models/worker';
import { RegistrationWorkflowService, WorkflowContext } from '../../shared/services/registration-workflow.service';

@Component({
  selector: 'app-registration-materials',
  templateUrl: './registration-materials.component.html',
  styleUrls: ['./registration-materials.component.scss'],
  standalone: false
})
export class RegistrationMaterialsComponent implements OnInit, OnDestroy {
  @ViewChild('grid', { static: false }) grid!: DxDataGridComponent;

  private unsubscribe$ = new Subject<void>();

  registrationId: number | null = null;
  registration: Registration | undefined;

  selectedRowIndex = -1;
  isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private onlineListener = () => { this.isOnline = true; };
  private offlineListener = () => { this.isOnline = false; };

  materialsLookupDataSource: any = [];
  private materialsStore: any;

  option: HoursOption | undefined;
  currentWorker: Worker | undefined;
  hasControls = false;

  codiceSetCellValue: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workerService: WorkerAndToolService,
    private restApi: RestApiService,
    private toolbar: ToolbarService,
    private workflowService: RegistrationWorkflowService
  ) {
    const self = this;
    this.codiceSetCellValue = async function(newData: any, value: string) {
      const column = this as any;

      const material = await self.materialsStore.byKey(value);
      if (material) {
        let desc = material.materialDescription ?? '';
        if (value && desc.startsWith(value + ' - ')) {
          desc = desc.substring(value.length + 3);
        }
        newData.materialDescription = desc;
        newData.availableUnityOfMeasurement = material.unityOfMeasurement ?? '';
        newData.availableUnityOfMeasurement1 = material.unityOfMeasurement1 ?? null;
        newData.availableUnityOfMeasurement2 = material.unityOfMeasurement2 ?? null;
        newData.availableUnityOfMeasurement3 = material.unityOfMeasurement3 ?? null;
        const units = self.getUnitOptions(material);
        newData.unityOfMeasurement = units.length > 0 ? units[0] : '';
      } else {
        newData.materialDescription = '';
        newData.unityOfMeasurement = '';
        newData.availableUnityOfMeasurement = '';
        newData.availableUnityOfMeasurement1 = null;
        newData.availableUnityOfMeasurement2 = null;
        newData.availableUnityOfMeasurement3 = null;
      }

      column.defaultSetCellValue(newData, value);
    };
  }

  async ngOnInit(): Promise<void> {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineListener);
      window.addEventListener('offline', this.offlineListener);
    }

    this.workerService.getHoursOption().then(val => this.option = val);
    this.workerService.getCurrentWorker().then(w => this.currentWorker = w);

    this.toolbar.TitleChanged('Selezione Materiali');
    this.toolbar.BackVisibleChanged(true);
    this.toolbar.ForwardVisibleChanged(true);

    this.toolbar.forwardClickedEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.onForward());
    this.toolbar.backClickedEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.onBack());

    const id = this.route.snapshot.paramMap.get('id');

    this.registrationId = id ? +id : null;

    if (this.registrationId != null) {
      this.registration = await this.workerService.getTempRegistration(this.registrationId);
    }

    if (this.registration) {
      this.workerService.getHasControls(this.registration).then(c => this.hasControls = c);
      if (!this.registration.warehouseArticles) {
        this.registration.warehouseArticles = [];
      }
    }

    await this.loadMaterialsCatalog();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      window.removeEventListener('offline', this.offlineListener);
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getUnitOptions(material: Material): string[] {
    return [
      material.unityOfMeasurement ?? '',
      material.unityOfMeasurement1 ?? '',
      material.unityOfMeasurement2 ?? '',
      material.unityOfMeasurement3 ?? ''
    ].filter(u => u && u.length > 0);
  }

  private async loadMaterialsCatalog(): Promise<void> {
    try {
      if (this.isOnline) {
        this.materialsStore = this.restApi.getMaterialsStore();
        this.materialsLookupDataSource = this.materialsStore;
      } else {
        const list = await this.workerService.getMaterials();
        this.materialsStore = new ArrayStore({ key: 'materialCode', data: list });
        this.materialsLookupDataSource = new DataSource({ store: this.materialsStore });
      }
    } catch (err) {
      console.error('Errore caricamento materiali', err);
      const list = await this.workerService.getMaterials();
      this.materialsStore = new ArrayStore({ key: 'materialCode', data: list });
      this.materialsLookupDataSource = new DataSource({ store: this.materialsStore });
    }
  }

  onInitNewRow(e: any) {
    e.data.materialCode = '';
    e.data.quantity = 1;
    e.data.materialDescription = '';
    e.data.unityOfMeasurement = '';
  }

  onRowDblClick(e: any) {
    this.selectedRowIndex = e.rowIndex;
    this.grid.instance.editRow(this.selectedRowIndex);
  }

  selectedChanged(e: any) {
    if (e.selectedRowsData?.length) {
      this.selectedRowIndex = e.component.getRowIndexByKey(e.selectedRowKeys[0]);
    } else {
      this.selectedRowIndex = -1;
    }
  }

  addRow() {
    this.grid.instance.addRow();
  }

  editRow() {
    if (this.selectedRowIndex >= 0) this.grid.instance.editRow(this.selectedRowIndex);
  }

  deleteRow() {
    if (this.selectedRowIndex >= 0) {
      this.grid.instance.deleteRow(this.selectedRowIndex);
      this.selectedRowIndex = -1;
    }
  }

  async onEditorPreparing(e: DxDataGridTypes.EditorPreparingEvent) {
    if (e.dataField === 'materialCode') {
      e.editorName = 'dxLookup';
      e.editorOptions.searchEnabled = true;
      e.editorOptions.searchExpr = 'materialDescription';
      e.editorOptions.searchMode = 'contains';
      e.editorOptions.showClearButton = true;
      e.editorOptions.usePopover = false;
      e.editorOptions.wrapItemText = true;
      e.editorOptions.placeholder = 'Selezionare il Materiale';
      e.editorOptions.dropDownOptions = {
        fullScreen: true,
        showTitle: false
      };
    }

    if (e.parentType === 'dataRow') {

      if (e.dataField === 'quantity') {
        e.editorOptions.onFocusIn = (function(args: any): void {
          args.element.querySelector('.dx-texteditor-input').select();
        });
      }

      if (e.dataField === 'unityOfMeasurement') {
        const data = e.row!.data;
        const unitOptions = [
          data.availableUnityOfMeasurement ?? '',
          data.availableUnityOfMeasurement1 ?? '',
          data.availableUnityOfMeasurement2 ?? '',
          data.availableUnityOfMeasurement3 ?? ''
        ].filter((u: string) => u.length > 0);

        e.editorOptions.dataSource = unitOptions;
        e.editorOptions.disabled = unitOptions.length <= 1;
      }
    }
  }

  getUnitaOptions = (options: any) => {
    const data = options.data;
    const units = [
      data?.availableUnityOfMeasurement ?? '',
      data?.availableUnityOfMeasurement1 ?? '',
      data?.availableUnityOfMeasurement2 ?? '',
      data?.availableUnityOfMeasurement3 ?? ''
    ].filter((u: string) => u.length > 0);
    return units;
  };

  // salva in registration e ritorna
  async onForward(): Promise<void> {
    if (!this.registration) { alert('Nessuna registrazione caricata'); return; }

    // warehouseArticles è già aggiornato direttamente dalla griglia
    if (this.registrationId != null) {
      await this.workerService.editTempRegistration(this.registration, this.registrationId);
    } else {
      await this.workerService.addTempRegistration(this.registration);
    }

    const ctx: WorkflowContext = { options: this.option!, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };

    if (this.workflowService.isFinalStep('registration-material', ctx)) {
      if (this.registration.id && this.registration.id > 0) {
        this.workerService.editRegistration(this.registration, this.registration.id)
          .then(() => this.router.navigate(['/registrations']));
      } else {
        this.workerService.addRegistration(this.registration)
          .then(() => this.router.navigate(['/registrations']));
      }
    } else {
      const next = this.workflowService.getNextStep('registration-material', ctx);
      this.router.navigate(['/' + next, { id: this.registrationId }]);
    }
  }

  onBack(): void {
    const ctx: WorkflowContext = { options: this.option!, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };
    const prev = this.workflowService.getPreviousStep('registration-material', ctx);
    if (prev) {
      this.router.navigate(['/' + prev, { id: this.registrationId }]);
    } else {
      this.router.navigate(['/registration']);
    }
  }
}
